import { FieldStore, FormStore, ListFieldStore, required, validateSubForm } from '@tom-ui/utils'
import { action, makeObservable, observable, runInAction } from 'mobx'

export type RailVisitGeneralInformationFormStoreType = {
  trainName?: string | null
  trainOperator?: string | null
  voyages?: RailVisitVoyageDto[]
}

export type RailVisitVoyageDto = {
  id?: number | null
  inboundNumber?: string | null
  outboundNumber?: string | null
}

export type RailVisitVoyageTripIdType = {
  id?: number | null
  inboundNumber: FieldStore<string>
  outboundNumber: FieldStore<string>
}

const createVoyageItem = (value?: RailVisitVoyageDto): RailVisitVoyageTripIdType => {
  const newForm = {
    id: value?.id,
    inboundNumber: new FieldStore(value?.inboundNumber ?? ''),
    outboundNumber: new FieldStore(value?.outboundNumber ?? ''),
  }

  return newForm
}

export class RailVisitGeneralInformationFormStore extends FormStore<RailVisitGeneralInformationFormStoreType> {
  trainName: FieldStore<string>
  trainOperator: FieldStore<string | null | undefined>
  voyages = new ListFieldStore<RailVisitVoyageTripIdType>([createVoyageItem()])

  constructor(initialValues?: Partial<RailVisitGeneralInformationFormStoreType>) {
    super(initialValues)

    makeObservable(this, {
      trainName: observable,
      trainOperator: observable,
      voyages: observable,

      setData: action,
      addVoyage: action,
      removeVoyage: action,
      reset: action,
    })

    this.trainName = new FieldStore<string>(initialValues?.trainName ?? '', required())
    this.trainOperator = new FieldStore<string | null | undefined>(initialValues?.trainOperator)

    runInAction(() => {
      if (initialValues?.voyages) {
        this.voyages.setValue([])
        initialValues?.voyages?.forEach(voyage => {
          this.voyages.push(createVoyageItem(voyage))
        })
      }
    })
  }

  setData(data: Partial<RailVisitGeneralInformationFormStoreType>) {
    this.trainName.setValue(data.trainName ?? '')
    this.trainOperator.setValue(data.trainOperator)

    this.voyages.setValue([])
    data.voyages?.forEach(voyage => this.addVoyage(voyage))
  }

  addVoyage = (value?: RailVisitVoyageDto) => {
    this.voyages.push(createVoyageItem(value))
  }

  removeVoyage = (index: number) => {
    this.voyages.removeByIndex(index)
  }

  get data(): RailVisitGeneralInformationFormStoreType {
    return {
      trainName: this.trainName.value,
      trainOperator: this.trainOperator.value,
      voyages: this.voyages.value.map(voyage => ({
        id: voyage.id,
        inboundNumber: voyage.inboundNumber.value,
        outboundNumber: voyage.outboundNumber.value,
      })),
    }
  }

  get isDirty(): boolean {
    return (
      this.trainName.dirty ||
      this.trainOperator.dirty ||
      this.voyages.value.some(
        voyage => voyage.inboundNumber.dirty || voyage.outboundNumber.dirty,
      ) ||
      this.initialValues.voyages?.map(v => v.id) !== this.voyages.value.map(v => v.id)
    )
  }

  validate = () => {
    const isSubFormValid = validateSubForm(this)
    return this.validateUniqueTripIds() && isSubFormValid
  }

  clearStaleDuplicateErrors = (): void => {
    const inboundCounts = new Map<string, number>()
    const outboundCounts = new Map<string, number>()

    for (const voyage of this.voyages.value) {
      const inbound = voyage.inboundNumber.value?.trim()
      const outbound = voyage.outboundNumber.value?.trim()
      if (inbound) inboundCounts.set(inbound, (inboundCounts.get(inbound) ?? 0) + 1)
      if (outbound) outboundCounts.set(outbound, (outboundCounts.get(outbound) ?? 0) + 1)
    }

    for (const voyage of this.voyages.value) {
      const inbound = voyage.inboundNumber.value?.trim()
      const outbound = voyage.outboundNumber.value?.trim()
      if (
        voyage.inboundNumber.error === 'duplicateTripId' &&
        (!inbound || (inboundCounts.get(inbound) ?? 0) <= 1)
      ) {
        voyage.inboundNumber.setError()
      }
      if (
        voyage.outboundNumber.error === 'duplicateTripId' &&
        (!outbound || (outboundCounts.get(outbound) ?? 0) <= 1)
      ) {
        voyage.outboundNumber.setError()
      }
    }
  }

  private readonly validateUniqueTripIds = (): boolean => {
    const inboundCounts = new Map<string, number>()
    const outboundCounts = new Map<string, number>()

    for (const voyage of this.voyages.value) {
      const inbound = voyage.inboundNumber.value?.trim()
      const outbound = voyage.outboundNumber.value?.trim()
      if (inbound) inboundCounts.set(inbound, (inboundCounts.get(inbound) ?? 0) + 1)
      if (outbound) outboundCounts.set(outbound, (outboundCounts.get(outbound) ?? 0) + 1)
    }

    let isValid = true
    for (const voyage of this.voyages.value) {
      const inbound = voyage.inboundNumber.value?.trim()
      const outbound = voyage.outboundNumber.value?.trim()

      if (inbound && (inboundCounts.get(inbound) ?? 0) > 1) {
        voyage.inboundNumber.touch()
        voyage.inboundNumber.setError('duplicateTripId')
        isValid = false
      } else if (voyage.inboundNumber.error === 'duplicateTripId') {
        voyage.inboundNumber.setError()
      }

      if (outbound && (outboundCounts.get(outbound) ?? 0) > 1) {
        voyage.outboundNumber.touch()
        voyage.outboundNumber.setError('duplicateTripId')
        isValid = false
      } else if (voyage.outboundNumber.error === 'duplicateTripId') {
        voyage.outboundNumber.setError()
      }
    }

    return isValid
  }

  reset() {
    this.setData(this.initialValues)
  }
}
